import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, CalendarIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ZAxis } from 'recharts';
const AMCContractsDashboard: React.FC = () => {
  const amcKPIs = [{
    title: 'Renewal Rate %',
    value: '78.5%',
    change: '+3.2%',
    isPositive: true
  }, {
    title: 'Expiring Contracts Count',
    value: '142',
    change: '+18',
    isPositive: false
  }, {
    title: 'Revenue at Risk',
    value: '₹54.8 Cr',
    change: '+₹8.2 Cr',
    isPositive: false
  }];
  const renewalFunnelData = [{
    stage: 'Total Active Contracts',
    value: 1850,
    percentage: 100,
    color: 'bg-blue-500'
  }, {
    stage: 'Expiring in 90 Days',
    value: 425,
    percentage: 23,
    color: 'bg-blue-600'
  }, {
    stage: 'Contacted',
    value: 368,
    percentage: 19.9,
    color: 'bg-green-500'
  }, {
    stage: 'Quote Sent',
    value: 298,
    percentage: 16.1,
    color: 'bg-green-600'
  }, {
    stage: 'Negotiation',
    value: 215,
    percentage: 11.6,
    color: 'bg-yellow-500'
  }, {
    stage: 'Renewed',
    value: 168,
    percentage: 9.1,
    color: 'bg-yellow-600'
  }];
  const cityPenetrationData = [{
    city: 'Delhi NCR',
    totalCustomers: 2850,
    amcActive: 1425,
    penetration: 50.0,
    revenue: 18.5
  }, {
    city: 'Mumbai',
    totalCustomers: 2420,
    amcActive: 1162,
    penetration: 48.0,
    revenue: 15.8
  }, {
    city: 'Bangalore',
    totalCustomers: 2185,
    amcActive: 1114,
    penetration: 51.0,
    revenue: 14.2
  }, {
    city: 'Chennai',
    totalCustomers: 1895,
    amcActive: 854,
    penetration: 45.1,
    revenue: 11.5
  }, {
    city: 'Pune',
    totalCustomers: 1650,
    amcActive: 759,
    penetration: 46.0,
    revenue: 9.8
  }, {
    city: 'Hyderabad',
    totalCustomers: 1520,
    amcActive: 684,
    penetration: 45.0,
    revenue: 8.9
  }, {
    city: 'Ahmedabad',
    totalCustomers: 1385,
    amcActive: 596,
    penetration: 43.0,
    revenue: 7.5
  }, {
    city: 'Kolkata',
    totalCustomers: 1285,
    amcActive: 565,
    penetration: 44.0,
    revenue: 7.2
  }, {
    city: 'Jaipur',
    totalCustomers: 985,
    amcActive: 374,
    penetration: 38.0,
    revenue: 4.8
  }, {
    city: 'Lucknow',
    totalCustomers: 850,
    amcActive: 323,
    penetration: 38.0,
    revenue: 4.2
  }];
  const tenureRenewalData = [{
    tenure: 1,
    renewalRate: 62.5,
    contractCount: 285,
    avgValue: 2.8
  }, {
    tenure: 2,
    renewalRate: 71.2,
    contractCount: 425,
    avgValue: 3.2
  }, {
    tenure: 3,
    renewalRate: 78.8,
    contractCount: 520,
    avgValue: 3.5
  }, {
    tenure: 4,
    renewalRate: 82.5,
    contractCount: 385,
    avgValue: 3.8
  }, {
    tenure: 5,
    renewalRate: 85.2,
    contractCount: 235,
    avgValue: 4.2
  }];
  const upcomingExpiriesData = [{
    contractId: 'AMC-2024-1847',
    customer: 'Maruti Suzuki - Manesar Plant',
    location: 'Delhi NCR',
    currentValue: 4.2,
    expiryDate: '15-Nov-2024',
    daysToExpiry: 12,
    tenure: 3,
    lastRenewalRate: 78.5,
    contactPerson: 'Rajesh Kumar',
    phone: '+91-9876543210',
    status: 'Critical',
    probability: 85
  }, {
    contractId: 'AMC-2024-1856',
    customer: 'Hyundai Motor - Chennai',
    location: 'Chennai',
    currentValue: 3.8,
    expiryDate: '18-Nov-2024',
    daysToExpiry: 15,
    tenure: 4,
    lastRenewalRate: 82.0,
    contactPerson: 'Priya Sharma',
    phone: '+91-9876543211',
    status: 'Critical',
    probability: 90
  }, {
    contractId: 'AMC-2024-1862',
    customer: 'Tata Motors - Pune',
    location: 'Pune',
    currentValue: 3.5,
    expiryDate: '22-Nov-2024',
    daysToExpiry: 19,
    tenure: 2,
    lastRenewalRate: 71.0,
    contactPerson: 'Amit Patel',
    phone: '+91-9876543212',
    status: 'High',
    probability: 75
  }, {
    contractId: 'AMC-2024-1875',
    customer: 'Mahindra & Mahindra - Nashik',
    location: 'Mumbai',
    currentValue: 2.9,
    expiryDate: '25-Nov-2024',
    daysToExpiry: 22,
    tenure: 5,
    lastRenewalRate: 85.0,
    contactPerson: 'Suresh Reddy',
    phone: '+91-9876543213',
    status: 'High',
    probability: 88
  }, {
    contractId: 'AMC-2024-1889',
    customer: 'Kia Motors - Anantapur',
    location: 'Bangalore',
    currentValue: 2.6,
    expiryDate: '28-Nov-2024',
    daysToExpiry: 25,
    tenure: 3,
    lastRenewalRate: 78.0,
    contactPerson: 'Neha Singh',
    phone: '+91-9876543214',
    status: 'Medium',
    probability: 80
  }, {
    contractId: 'AMC-2024-1895',
    customer: 'Honda Cars - Greater Noida',
    location: 'Delhi NCR',
    currentValue: 2.4,
    expiryDate: '02-Dec-2024',
    daysToExpiry: 29,
    tenure: 2,
    lastRenewalRate: 70.5,
    contactPerson: 'Vikram Mehta',
    phone: '+91-9876543215',
    status: 'Medium',
    probability: 72
  }, {
    contractId: 'AMC-2024-1902',
    customer: 'Toyota Kirloskar - Bangalore',
    location: 'Bangalore',
    currentValue: 3.2,
    expiryDate: '05-Dec-2024',
    daysToExpiry: 32,
    tenure: 4,
    lastRenewalRate: 82.5,
    contactPerson: 'Anjali Desai',
    phone: '+91-9876543216',
    status: 'Medium',
    probability: 85
  }, {
    contractId: 'AMC-2024-1918',
    customer: 'VW India - Chakan',
    location: 'Pune',
    currentValue: 1.9,
    expiryDate: '08-Dec-2024',
    daysToExpiry: 35,
    tenure: 1,
    lastRenewalRate: 62.0,
    contactPerson: 'Rahul Joshi',
    phone: '+91-9876543217',
    status: 'Low',
    probability: 65
  }, {
    contractId: 'AMC-2024-1925',
    customer: 'MG Motor - Halol',
    location: 'Ahmedabad',
    currentValue: 2.1,
    expiryDate: '12-Dec-2024',
    daysToExpiry: 39,
    tenure: 3,
    lastRenewalRate: 77.5,
    contactPerson: 'Sanjay Gupta',
    phone: '+91-9876543218',
    status: 'Low',
    probability: 78
  }, {
    contractId: 'AMC-2024-1932',
    customer: 'Renault Nissan - Chennai',
    location: 'Chennai',
    currentValue: 1.8,
    expiryDate: '15-Dec-2024',
    daysToExpiry: 42,
    tenure: 2,
    lastRenewalRate: 71.2,
    contactPerson: 'Pooja Iyer',
    phone: '+91-9876543219',
    status: 'Low',
    probability: 70
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critical':
        return 'text-red-600 bg-red-50 border-red-300';
      case 'High':
        return 'text-orange-600 bg-orange-50 border-orange-300';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-300';
      case 'Low':
        return 'text-green-600 bg-green-50 border-green-300';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-300';
    }
  };
  const getProbabilityColor = (probability: number) => {
    if (probability >= 85) return 'text-green-600 font-bold';
    if (probability >= 75) return 'text-yellow-600 font-semibold';
    if (probability >= 65) return 'text-orange-600 font-semibold';
    return 'text-red-600 font-bold';
  };
  const getPenetrationColor = (penetration: number) => {
    if (penetration >= 50) return 'bg-green-500';
    if (penetration >= 45) return 'bg-yellow-500';
    return 'bg-orange-500';
  };
  return <div data-id="element-122">
      <DescriptionPanel title="AMC Contracts & Renewals Analysis" description="Comprehensive tracking of Annual Maintenance Contract (AMC) renewals, penetration rates, and revenue at risk. Monitor expiring contracts, renewal probabilities, and city-wise AMC adoption to maximize contract retention and revenue." insights={['Renewal rate at 78.5% - 3.2% improvement over last quarter', '142 contracts expiring in next 90 days representing ₹54.8 Cr revenue at risk', 'Bangalore leading AMC penetration at 51% with ₹14.2 Cr annual revenue', 'Customers with 5+ year tenure showing 85.2% renewal rate - focus on long-term relationships']} data-id="element-123" />
      {/* AMC KPI Row */}
      <div className="mb-8" data-id="element-124">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-125">
          AMC Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-126">
          {amcKPIs.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-127">
              <div className="flex flex-col h-full" data-id="element-128">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-129">
                  {kpi.title}
                </div>
                <div className="mt-3 text-3xl font-bold text-gray-900" data-id="element-130">
                  {kpi.value}
                </div>
                <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-131">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-132" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-133" />}
                  {kpi.change}
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* AMC Renewal Funnel */}
      <Card className="p-6 mb-8" data-id="element-134">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-135">
          AMC Renewal Funnel
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-136">
          Contract renewal journey from expiry to closure
        </p>
        <div className="space-y-3 py-4" data-id="element-137">
          {renewalFunnelData.map((stage, index) => <div key={index} className="relative" data-id="element-138">
              <div className="flex items-center justify-between mb-2" data-id="element-139">
                <div className="flex items-center" data-id="element-140">
                  <span className="text-sm font-medium text-gray-700" data-id="element-141">
                    {stage.stage}
                  </span>
                  {index > 0 && <span className="ml-2 text-xs text-gray-500" data-id="element-142">
                      (
                      {(stage.value / renewalFunnelData[index - 1].value * 100).toFixed(1)}
                      % conversion)
                    </span>}
                </div>
                <span className="text-sm font-semibold text-gray-800" data-id="element-143">
                  {stage.value} contracts
                </span>
              </div>
              <div className="relative h-12 bg-gray-100 rounded-lg overflow-hidden" data-id="element-144">
                <div className={`h-full ${stage.color} flex items-center justify-between px-4 transition-all duration-500`} style={{
              width: `${Math.max(stage.percentage, 5)}%`
            }} data-id="element-145">
                  <span className="text-white font-medium text-sm" data-id="element-146">
                    {stage.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>)}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-147">
          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-148">
            <div className="text-center" data-id="element-149">
              <div className="text-gray-600" data-id="element-150">Renewal Success Rate</div>
              <div className="text-xl font-bold text-green-600" data-id="element-151">39.5%</div>
            </div>
            <div className="text-center" data-id="element-152">
              <div className="text-gray-600" data-id="element-153">Avg Days to Close</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-154">45</div>
            </div>
            <div className="text-center" data-id="element-155">
              <div className="text-gray-600" data-id="element-156">In Negotiation</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-157">215</div>
            </div>
          </div>
        </div>
      </Card>
      {/* City-wise AMC Penetration and Tenure vs Renewal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-158">
        <Card className="p-6" data-id="element-159">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-160">
            City-wise AMC Penetration
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-161">
            Active AMC contracts vs total customer base
          </p>
          <div className="space-y-3" data-id="element-162">
            {cityPenetrationData.map((city, index) => <div key={index} className="space-y-1" data-id="element-163">
                <div className="flex items-center justify-between text-sm" data-id="element-164">
                  <span className="font-medium text-gray-800" data-id="element-165">{city.city}</span>
                  <div className="flex items-center space-x-3" data-id="element-166">
                    <span className="text-gray-600" data-id="element-167">
                      {city.amcActive}/{city.totalCustomers}
                    </span>
                    <span className="font-bold text-gray-900 w-12 text-right" data-id="element-168">
                      {city.penetration}%
                    </span>
                  </div>
                </div>
                <div className="relative h-6 bg-gray-100 rounded-lg overflow-hidden" data-id="element-169">
                  <div className={`h-full ${getPenetrationColor(city.penetration)} transition-all duration-500`} style={{
                width: `${city.penetration}%`
              }} data-id="element-170" />
                </div>
                <div className="flex items-center justify-end text-xs text-gray-500" data-id="element-171">
                  <span data-id="element-172">Revenue: ₹{city.revenue} Cr</span>
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-173">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-174">
              <div className="text-center" data-id="element-175">
                <div className="text-gray-600" data-id="element-176">Avg Penetration</div>
                <div className="text-xl font-bold text-gray-900" data-id="element-177">45.2%</div>
              </div>
              <div className="text-center" data-id="element-178">
                <div className="text-gray-600" data-id="element-179">Total Revenue</div>
                <div className="text-xl font-bold text-green-600" data-id="element-180">
                  ₹102.4 Cr
                </div>
              </div>
              <div className="text-center" data-id="element-181">
                <div className="text-gray-600" data-id="element-182">Top City</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-183">Bangalore</div>
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-6" data-id="element-184">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-185">
            Tenure vs Renewal Likelihood
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-186">
            Customer tenure correlation with renewal rates
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-187">
            <ScatterChart data-id="element-188">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-189" />
              <XAxis type="number" dataKey="tenure" name="Tenure (Years)" tick={{
              fontSize: 12
            }} label={{
              value: 'Tenure (Years)',
              position: 'insideBottom',
              offset: -5
            }} domain={[0, 6]} data-id="element-190" />
              <YAxis type="number" dataKey="renewalRate" name="Renewal Rate %" tick={{
              fontSize: 12
            }} label={{
              value: 'Renewal Rate %',
              angle: -90,
              position: 'insideLeft'
            }} domain={[60, 90]} data-id="element-191" />
              <ZAxis type="number" dataKey="contractCount" range={[200, 1000]} name="Contracts" data-id="element-192" />
              <Tooltip cursor={{
              strokeDasharray: '3 3'
            }} contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: any, name: string) => {
              if (name === 'Renewal Rate %') return `${value}%`;
              if (name === 'Avg Value') return `₹${value} Cr`;
              return value;
            }} data-id="element-193" />
              <Legend data-id="element-194" />
              <Scatter name="Tenure Analysis" data={tenureRenewalData} fill="#3b82f6" data-id="element-195">
                {tenureRenewalData.map((entry, index) => <Cell key={`cell-${index}`} fill={index >= 3 ? '#10b981' : '#3b82f6'} data-id="element-196" />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-197">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-198">
              <div className="text-center" data-id="element-199">
                <div className="text-gray-600" data-id="element-200">1-2 Year Tenure</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-201">66.9%</div>
              </div>
              <div className="text-center" data-id="element-202">
                <div className="text-gray-600" data-id="element-203">3-4 Year Tenure</div>
                <div className="text-xl font-bold text-green-600" data-id="element-204">80.7%</div>
              </div>
              <div className="text-center" data-id="element-205">
                <div className="text-gray-600" data-id="element-206">5+ Year Tenure</div>
                <div className="text-xl font-bold text-green-600" data-id="element-207">85.2%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Upcoming Expiries Table */}
      <Card className="p-6" data-id="element-208">
        <div className="flex items-center justify-between mb-4" data-id="element-209">
          <div data-id="element-210">
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-211">
              Upcoming Contract Expiries - Next 45 Days
            </h3>
            <p className="text-sm text-gray-600" data-id="element-212">
              High-priority renewals requiring immediate attention
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-213">
            <AlertTriangleIcon size={20} className="text-red-600" data-id="element-214" />
            <span className="text-sm font-semibold text-red-600" data-id="element-215">
              10 Contracts Expiring
            </span>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-216">
          <table className="w-full" data-id="element-217">
            <thead data-id="element-218">
              <tr className="border-b-2 border-gray-300" data-id="element-219">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-220">
                  Contract ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-221">
                  Customer
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-222">
                  Location
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-223">
                  Value
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-224">
                  Expiry Date
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-225">
                  Days Left
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-226">
                  Tenure
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-227">
                  Contact Person
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-228">
                  Status
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-229">
                  Renewal Prob
                </th>
              </tr>
            </thead>
            <tbody data-id="element-230">
              {upcomingExpiriesData.map((item, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-231">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-232">
                    {item.contractId}
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-233">
                    {item.customer}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-234">
                    {item.location}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-235">
                    ₹{item.currentValue} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-236">
                    {item.expiryDate}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-237">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${item.daysToExpiry <= 15 ? 'bg-red-100 text-red-800' : item.daysToExpiry <= 30 ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-238">
                      {item.daysToExpiry}d
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-center text-gray-700" data-id="element-239">
                    {item.tenure} yrs
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-240">
                    <div data-id="element-241">{item.contactPerson}</div>
                    <div className="text-xs text-gray-500" data-id="element-242">{item.phone}</div>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-243">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(item.status)}`} data-id="element-244">
                      {item.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-245">
                    <span className={getProbabilityColor(item.probability)} data-id="element-246">
                      {item.probability}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-247">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-248">
            <div className="text-center" data-id="element-249">
              <div className="text-gray-600" data-id="element-250">Total Contracts</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-251">10</div>
            </div>
            <div className="text-center" data-id="element-252">
              <div className="text-gray-600" data-id="element-253">Total Value</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-254">₹28.4 Cr</div>
            </div>
            <div className="text-center" data-id="element-255">
              <div className="text-gray-600" data-id="element-256">Critical Status</div>
              <div className="text-xl font-bold text-red-600" data-id="element-257">2</div>
            </div>
            <div className="text-center" data-id="element-258">
              <div className="text-gray-600" data-id="element-259">Avg Probability</div>
              <div className="text-xl font-bold text-green-600" data-id="element-260">78.8%</div>
            </div>
            <div className="text-center" data-id="element-261">
              <div className="text-gray-600" data-id="element-262">Expected Revenue</div>
              <div className="text-xl font-bold text-green-600" data-id="element-263">₹22.4 Cr</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default AMCContractsDashboard;